import { Injectable, inject } from '@angular/core';
import { CheckoutFormOut, GetReservationDetailsResponse } from '@appTypes/api.types';
import { ApiErrorResponse } from '@core/interceptors/api.interceptor';
import { DataLoadStatusModel, RequestStateModel } from '@core/store/store.models';
import { RequestStateParams } from '@core/store/store.types';
import { Action, Selector, State, StateContext, Store, createSelector } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { CartState } from '@shared/modules/cart/state/cart.state';
import { LoadingSpinnerService } from '@shared/modules/loading-spinner/loading-spinner.service';
import { catchError, tap, throwError } from 'rxjs';
import { AppState } from '@app-state/app.state';
import { processCheckoutItems } from '../checkout.utils';
import { CheckoutApiService } from '../services/checkout-api.service';
import {
  calcCheckout,
  checkOutClearFormData,
  checkOutGetReservationDetailsData,
  checkoutVerifyCalculatedPrices,
  confirmCheckoutFormData
} from './checkout.actions';
import { CheckoutStateModel } from './checkout.model';

const defaults = new CheckoutStateModel();

/**
 * QA doc features/website/checkout.md R-CK32 (website-web#142): online-api answers a refused CalcCheckout with HTTP 200 and `error: true`
 * (CheckoutController.BusinessErrorResponse: `errorCode`, the reason in `userErrorDescription`, C23 `traceId`), so the interceptor never
 * turns it into an `ApiErrorResponse` with a `displayErrorMessage`. The answer is thrown with its reason and its Trace ID added, so the
 * error box says why (e.g. CHECKOUT_INVALID_BIRTH_DATE) instead of "It seems that something has gone wrong".
 */
const serverRefusal = (response: CheckoutFormOut): ApiErrorResponse =>
  ({
    ...response,
    displayErrorMessage: response.userErrorDescription?.trim() ? response.userErrorDescription : undefined,
    activityId: response.activityId ?? response.traceId ?? undefined
  }) as unknown as ApiErrorResponse;

@State<CheckoutStateModel>({
  name: 'checkout',
  defaults
})
@Injectable()
export class CheckoutState {
  readonly #api = inject(CheckoutApiService);
  readonly #loadingSpinnerService = inject(LoadingSpinnerService);
  readonly #store = inject(Store);

  @Action(calcCheckout)
  calcCheckout({ patchState, setState }: StateContext<CheckoutStateModel>, { payload }: calcCheckout) {
    const isInitialRequest = !payload;
    const updateState = (stateParams: RequestStateParams<CheckoutFormOut>, updateJustState = false) => {
      if (!updateJustState) {
        patchState({
          calcCheckoutResult: new RequestStateModel(stateParams)
        });
      } else if (stateParams instanceof DataLoadStatusModel)
        setState(
          patch<CheckoutStateModel>({
            calcCheckoutResult: patch<RequestStateModel<CheckoutFormOut>>({
              loadState: stateParams
            })
          })
        );
      this.#loadingSpinnerService.hide();
    };

    updateState(new DataLoadStatusModel('loading'), !isInitialRequest);

    this.#loadingSpinnerService.show();
    const items = this.#store.selectSnapshot(CartState.items);

    const _payload = processCheckoutItems(items, payload);

    return this.#api.calcCheckout(_payload).pipe(
      tap((response) => {
        if (response.error) {
          throw serverRefusal(response);
        }
        updateState(response);
      }),
      catchError((e: ApiErrorResponse) => {
        const errorMessage =
          e?.displayErrorMessage ??
          "It seems that something has gone wrong. Don't worry, our support team is ready to help you";
        updateState(new DataLoadStatusModel('error', errorMessage, e), !isInitialRequest);
        return throwError(() => e);
      })
    );
  }

  // the newest verification wins (qa.md R-HT15a): an older one still in flight is cancelled, so its late answer can never replace the state a newer one reads
  @Action(checkoutVerifyCalculatedPrices, { cancelUncompleted: true })
  checkoutVerifyCalculatedPrices(
    { patchState }: StateContext<CheckoutStateModel>,
    { payload }: checkoutVerifyCalculatedPrices
  ) {
    const updateState = (stateParams: RequestStateParams<CheckoutFormOut>) => {
      patchState({
        verifyCalculatedPricesResult: new RequestStateModel(stateParams)
      });
    };

    updateState(new DataLoadStatusModel('loading'));
    const items = this.#store.selectSnapshot(CartState.items);
    const _payload = processCheckoutItems(items, payload, true);

    return this.#api.calcCheckout(_payload).pipe(
      tap((response) => {
        updateState(response);
      }),
      catchError((e: ApiErrorResponse) => {
        const errorMessage =
          e?.displayErrorMessage ??
          "It seems that something has gone wrong. Don't worry, our support team is ready to help you";
        updateState(new DataLoadStatusModel('error', errorMessage));
        return throwError(() => e);
      })
    );
  }
  @Action(checkOutClearFormData)
  checkOutClearFormData({ patchState }: StateContext<CheckoutStateModel>) {
    patchState({ calcCheckoutResult: new RequestStateModel<CheckoutFormOut>() });
  }
  @Action(confirmCheckoutFormData)
  confirmCheckoutFormData({ patchState }: StateContext<CheckoutStateModel>, { payload }: confirmCheckoutFormData) {
    const updateState = (stateParams: RequestStateParams<CheckoutFormOut>) => {
      patchState({
        confirmCheckoutResult: new RequestStateModel(stateParams)
      });
      this.#loadingSpinnerService.hide();
    };
    updateState(new DataLoadStatusModel('loading'));
    this.#loadingSpinnerService.show();

    const items = this.#store.selectSnapshot(CartState.items);

    const _payload = processCheckoutItems(items, payload);
    const generatedURL = this.#store.selectSnapshot(AppState.isGeneratedURL);
    return this.#api.confirmCheckout({ ..._payload, generatedURL }).pipe(
      tap((response) => {
        updateState(response);
      }),
      catchError((e) => {
        const errorMessage =
          e?.displayErrorMessage ??
          "It seems that something has gone wrong. Don't worry, our support team is ready to help you";
        updateState(new DataLoadStatusModel('error', errorMessage, e));
        return throwError(() => e);
      })
    );
  }

  @Action(checkOutGetReservationDetailsData)
  checkOutGetReservationDetailsData(
    { patchState }: StateContext<CheckoutStateModel>,
    { payload }: checkOutGetReservationDetailsData
  ) {
    const updateState = (stateParams: RequestStateParams<GetReservationDetailsResponse>) => {
      patchState({
        reservationDetailsData: new RequestStateModel(stateParams)
      });
      this.#loadingSpinnerService.hide();
    };
    updateState(new DataLoadStatusModel('loading'));
    this.#loadingSpinnerService.show();

    return this.#api.getReservationDetails({ id: payload.id }).pipe(
      tap((response) => {
        updateState(response);
      }),
      catchError((e) => {
        const errorMessage =
          e?.displayErrorMessage ??
          "It seems that something has gone wrong. Don't worry, our support team is ready to help you";
        updateState(new DataLoadStatusModel('error', errorMessage, e));
        return throwError(() => e);
      })
    );
  }
  @Selector([CheckoutState])
  static calcCheckoutResult({ calcCheckoutResult }: CheckoutStateModel) {
    return calcCheckoutResult;
  }
  @Selector([CheckoutState])
  static calcCheckoutLoadState({ calcCheckoutResult: { loadState } }: CheckoutStateModel) {
    return loadState;
  }

  @Selector([CheckoutState])
  static visas({ calcCheckoutResult: { loadState, response } }: CheckoutStateModel) {
    return {
      loadState,
      response: response?.visas ?? []
    };
  }
  @Selector([CheckoutState])
  static terminals({ calcCheckoutResult: { loadState, response } }: CheckoutStateModel) {
    return {
      loadState,
      response: response?.terminals ?? []
    };
  }
  @Selector([CheckoutState])
  static priceCurrency({ calcCheckoutResult: { response } }: CheckoutStateModel) {
    return response?.priceCurrency;
  }

  @Selector([CheckoutState])
  static verifyCalculatedPricesResult({ verifyCalculatedPricesResult }: CheckoutStateModel) {
    return verifyCalculatedPricesResult;
  }

  @Selector([CheckoutState])
  static verifyCalculatedPricesState({ verifyCalculatedPricesResult }: CheckoutStateModel) {
    return verifyCalculatedPricesResult.loadState;
  }

  @Selector([CheckoutState])
  static confirmCheckoutResult({ confirmCheckoutResult }: CheckoutStateModel) {
    return confirmCheckoutResult;
  }

  static getVisaName(visaId: string) {
    return createSelector([CheckoutState], ({ calcCheckoutResult: { response } }: CheckoutStateModel) => {
      return (
        response?.visas?.find((v) => {
          return v.prid === visaId;
        })?.prName ?? 'UNKNOWN'
      );
    });
  }
  static getTouristTransfersData(touristId?: string) {
    return createSelector([CheckoutState], ({ calcCheckoutResult: { response } }: CheckoutStateModel) => {
      if (!touristId) return [];
      return response?.tourists?.find((t) => t.id === touristId)?.transfers ?? [];
    });
  }
  @Selector([CheckoutState])
  static reservationDetailsData({ reservationDetailsData }: CheckoutStateModel) {
    return reservationDetailsData;
  }

  @Selector([CheckoutState])
  static reservationDetailsResponse({ reservationDetailsData: { response } }: CheckoutStateModel) {
    return response?.reservation;
  }
}
